<script setup lang="ts">
import { Popover } from 'reka-ui/namespaced';

export type PopoverPlacement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'right'
  | 'right-start'
  | 'right-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end';

const isVisible = defineModel<boolean>({ default: false });

const props = withDefaults(
  defineProps<{
    placement?: PopoverPlacement;
    matchReferenceWidth?: boolean;
  }>(),
  {
    placement: 'bottom-start',
    matchReferenceWidth: false,
  },
);

defineSlots<{
  control(props: { toggle(): void; isVisible: boolean }): any;
  content(props: { close(): void }): any;
}>();

// TODO: change usages to use side and align instead (30 minutes)
const resolvedPlacement = computed(() => {
  const [side, align = 'center'] = props.placement.split('-');
  if (side !== 'top' && side !== 'right' && side !== 'bottom' && side !== 'left')
    throw new Error('side unexpectedly invalid');
  if (align !== 'start' && align !== 'center' && align !== 'end')
    throw new Error('end unexpectedly invalid');
  return { side, align } as const;
});
</script>

<template>
  <Popover.Root v-model:open="isVisible">
    <!-- eslint disabled as focus handled differently for keyboard-only users -->
    <!-- eslint-disable-next-line vuejs-accessibility/mouse-events-have-key-events -->
    <Popover.Anchor as-child>
      <slot name="control" :toggle="() => (isVisible = !isVisible)" :is-visible />
    </Popover.Anchor>
    <Popover.Portal to="#teleport-target">
      <!-- eslint disabled as focus handled differently for keyboard-only users -->
      <!-- eslint-disable-next-line vuejs-accessibility/mouse-events-have-key-events -->
      <Popover.Content
        :side-offset="6"
        :align="resolvedPlacement.align"
        align-flip
        :side="resolvedPlacement.side"
        :collision-padding="10"
        prioritize-position
        class="z-modal box-border rounded-sm border bg-default shadow-float"
        :class="{ 'w-[var(--reka-popover-trigger-width)]': matchReferenceWidth }"
        data-testid="popover"
      >
        <slot name="content" :close="() => (isVisible = false)" />
      </Popover.Content>
    </Popover.Portal>
  </Popover.Root>
</template>
